import { useEffect, useRef, useState } from "react"
import equal from "fast-deep-equal"
import { readPostBotError } from "../utils/apiErrors"

export interface RevisionResource {
  id: string
  revision: number
}
export type RevisionChoices = Record<string, "server" | "local">
export interface RevisionDifference {
  /** JSON pointer, also the key in RevisionChoices. */
  key: string
  field: string
  bindingId?: string
  baseline: unknown
  remote: unknown
  draft: unknown
  localChanged: boolean
}
export interface RevisionState<T> {
  status: "idle" | "saving" | "conflict" | "resolvedDraft"
  baseline: T
  draft: T
  remote?: T
  differences: RevisionDifference[]
  error?: unknown
  fetchError?: unknown
}
export interface RevisionConflictOptions<T extends RevisionResource> {
  /** Include resource kind as well as ID when kinds can share a mounted form. */
  resourceKey: string
  /** Applied once per resourceKey. Cache refetches are deliberately ignored. */
  initial: T
  fields: readonly (keyof T & string)[]
  /** Map fields resolved per UUID, each full value is atomic; omission is not deletion.
   * Publication forms must pass ["bindings"]. Placement fields stay atomic. */
  bindingFields?: readonly (keyof T & string)[]
  /** Pass an unwrapped detail request, not a hook; never invoked for ordinary errors. */
  fetchRemote: (resourceKey: string) => Promise<T>
}
export interface RevisionSaveAttempt<T> {
  succeed: (saved: T) => void
  fail: (error: unknown) => Promise<void>
}

const pointer = (key: string) => key.replace(/~/g, "~0").replace(/\//g, "~1")
const asMap = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
const initialState = <T>(initial: T): RevisionState<T> => ({
  status: "idle",
  baseline: initial,
  draft: initial,
  differences: [],
})

function differences<T extends RevisionResource>(
  baseline: T,
  remote: T,
  draft: T,
  options: RevisionConflictOptions<T>,
): RevisionDifference[] {
  return options.fields
    .filter((field) => field !== "id" && field !== "revision")
    .flatMap((field) => {
      const entry = (
        before: unknown,
        server: unknown,
        local: unknown,
        bindingId?: string,
      ): RevisionDifference[] => {
        const localChanged = local !== undefined && !equal(before, local)
        if (!localChanged && equal(before, server)) return []
        return [
          {
            key: `/${pointer(field)}${bindingId === undefined ? "" : `/${pointer(bindingId)}`}`,
            field,
            bindingId,
            baseline: before,
            remote: server,
            draft: local,
            localChanged,
          },
        ]
      }
      if (!options.bindingFields?.includes(field))
        return entry(baseline[field], remote[field], draft[field])
      const before = asMap(baseline[field]),
        server = asMap(remote[field]),
        local = asMap(draft[field])
      return [
        ...new Set([
          ...Object.keys(before),
          ...Object.keys(server),
          ...Object.keys(local),
        ]),
      ].flatMap((id) => entry(before[id], server[id], local[id], id))
    })
}

function applyLocalValue<T>(target: T, difference: RevisionDifference) {
  const field = difference.field as keyof T
  target[field] = (
    difference.bindingId === undefined
      ? difference.draft
      : { ...asMap(target[field]), [difference.bindingId]: difference.draft }
  ) as T[keyof T]
}

/**
 * No mutation/reset/retry is performed here. Forms call beginSave(values) just
 * before their mutation, then its bound succeed/fail from afterSubmit/onSubmitError.
 * Use initializeOnce + resetOnSuccess=false in RtkHookForm. On resolve, explicitly
 * install the returned draft in RHF; only a later submit may call beginSave again.
 * Treat input snapshots as immutable. Remounting by resource identity is recommended.
 */
export function useRevisionConflict<T extends RevisionResource>(
  options: RevisionConflictOptions<T>,
) {
  const { resourceKey, initial } = options
  const [stored, setStored] = useState(() => ({
    key: resourceKey,
    state: initialState(initial),
  }))
  // A separate scope invalidates all callbacks when the resource changes, even
  // if the next resource later reuses the same ID. Synchronous state updates also
  // prevent two beginSave calls in one event from admitting duplicate submissions.
  const scopeRef = useRef({
    key: resourceKey,
    state: stored.state,
    sequence: 0,
  })
  if (scopeRef.current.key !== resourceKey)
    scopeRef.current = {
      key: resourceKey,
      state: initialState(initial),
      sequence: 0,
    }
  const scope = scopeRef.current
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      scopeRef.current.sequence++
    }
  }, [])
  const active = () => mounted.current && scopeRef.current === scope
  const update = (state: RevisionState<T>) => {
    if (!active()) return
    scope.state = state
    setStored({ key: resourceKey, state })
  }
  const setDraft = (draft: T) => {
    if (!active() || draft.id !== scope.state.baseline.id) return
    const state = scope.state
    if (equal(state.draft, draft)) return
    update({
      ...state,
      draft,
      differences: state.remote
        ? differences(state.baseline, state.remote, draft, options)
        : [],
    })
  }
  const beginSave = (
    draft = scope.state.draft,
  ): RevisionSaveAttempt<T> | null => {
    if (
      !active() ||
      scope.state.status === "saving" ||
      scope.state.status === "conflict"
    )
      return null
    if (draft.id !== scope.state.baseline.id)
      throw new TypeError("Wrong resource draft")
    const sequence = ++scope.sequence
    const baseline = scope.state.baseline
    let settled = false
    const current = () => active() && scope.sequence === sequence
    const assertResource = (resource: T) => {
      if (
        resource.id !== baseline.id ||
        !Number.isSafeInteger(resource.revision) ||
        resource.revision < 1
      )
        throw new TypeError("Invalid remote resource")
    }
    update({ status: "saving", baseline, draft, differences: [] })
    return {
      succeed(saved) {
        if (!current() || settled) return
        settled = true
        try {
          assertResource(saved)
        } catch (error) {
          update({ ...scope.state, status: "idle", error })
          return
        }
        // A user can keep typing while a request is in flight.
        const nextDraft = { ...saved }
        for (const difference of differences(
          draft,
          saved,
          scope.state.draft,
          options,
        )) {
          if (difference.localChanged) applyLocalValue(nextDraft, difference)
        }
        update({
          status: "idle",
          baseline: saved,
          draft: nextDraft,
          differences: [],
        })
      },
      async fail(error) {
        if (!current() || settled) return
        settled = true
        const parsed = readPostBotError(error)
        if (
          parsed.source !== "feature" ||
          parsed.code !== "REVISION_CONFLICT"
        ) {
          update({ ...scope.state, status: "idle", error })
          return
        }
        try {
          const remote = await options.fetchRemote(resourceKey)
          if (!current()) return
          assertResource(remote)
          const latestDraft = scope.state.draft
          update({
            status: "conflict",
            baseline,
            draft: latestDraft,
            remote,
            differences: differences(baseline, remote, latestDraft, options),
            error,
          })
        } catch (fetchError) {
          if (current())
            update({ ...scope.state, status: "idle", error, fetchError })
        }
      },
    }
  }
  const resolve = (
    choices: RevisionChoices,
  ): { baseline: T; draft: T } | undefined => {
    if (!active()) return
    const state = scope.state
    if (state.status !== "conflict" || !state.remote)
      throw new Error("No revision conflict to resolve")
    const draft = { ...state.remote }
    for (const difference of state.differences) {
      if (!difference.localChanged) continue
      const choice = choices[difference.key]
      if (choice !== "server" && choice !== "local")
        throw new Error(`Choose a value for ${difference.key}`)
      if (choice === "server") continue
      applyLocalValue(draft, difference)
    }
    const resolved = { baseline: state.remote, draft }
    update({ ...resolved, status: "resolvedDraft", differences: [] })
    return resolved
  }
  return { state: scope.state, setDraft, beginSave, resolve }
}
